import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { playerDashboardApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

function fmt(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function statusBadge(status) {
  const map = {
    registration:  { label: 'Registration Open', bg: '#eff6ff', color: '#1d4ed8' },
    seeding:       { label: 'Seeding',            bg: '#fefce8', color: '#854d0e' },
    draw_ready:    { label: 'Draw Ready',          bg: '#f0fdf4', color: '#15803d' },
    in_progress:   { label: 'Live',               bg: '#fef3c7', color: '#92400e' },
    completed:     { label: 'Completed',           bg: '#f3f4f6', color: '#6b7280' },
    cancelled:     { label: 'Cancelled',           bg: '#fef2f2', color: '#dc2626' },
  };
  const s = map[status] || { label: status, bg: '#f3f4f6', color: '#6b7280' };
  return (
    <span style={{ fontSize: '0.72rem', fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '1.25rem 1.5rem', flex: '1 1 160px', minWidth: 140, textAlign: 'center' }}>
      <div style={{ fontSize: '2rem', fontWeight: 700, color: '#0D9488', lineHeight: 1.1 }}>{value ?? 0}</div>
      <div style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 500, marginTop: 4 }}>{label}</div>
    </div>
  );
}

const PlayerDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, tourRes] = await Promise.all([
          playerDashboardApi.getDashboardStats(),
          playerDashboardApi.getMyTournaments(),
        ]);
        setStats(statsRes.data);
        setTournaments(tourRes.data.tournaments || []);
      } catch (err) {
        console.error('Dashboard load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const active = tournaments.filter(t => ['registration', 'seeding', 'draw_ready', 'in_progress'].includes(t.status));
  const recent = tournaments.filter(t => t.status === 'completed').slice(0, 3);

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}><div className="spinner" /></div>;
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '2rem 1rem' }}>

      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', margin: 0 }}>
          Welcome back, {user?.name?.split(' ')[0]}
        </h1>
        <p style={{ color: '#6b7280', marginTop: 4, fontSize: '0.9rem' }}>Here's an overview of your tournament activity.</p>
      </div>

      {/* Stat cards */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
        <StatCard label="Tournaments Entered" value={stats?.tournamentsEntered} />
        <StatCard label="Events Entered" value={stats?.eventsEntered} />
        <StatCard label="Active Tournaments" value={stats?.activeTournaments} />
        <StatCard label="Completed" value={stats?.completedTournaments} />
      </div>

      {/* No data state */}
      {tournaments.length === 0 && (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '3rem', textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🏸</div>
          <p style={{ color: '#374151', fontWeight: 600, marginBottom: 4 }}>No tournaments yet</p>
          <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
            Find a tournament and register to get started.
          </p>
          <Link
            to="/tournaments"
            style={{ display: 'inline-block', padding: '8px 20px', background: '#0D9488', color: 'white', borderRadius: 8, fontSize: '0.875rem', fontWeight: 600, textDecoration: 'none' }}
          >
            Find Tournaments
          </Link>
        </div>
      )}

      {/* Active tournaments */}
      {active.length > 0 && (
        <section style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#111827', marginBottom: '0.75rem' }}>Active Tournaments</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {active.map(t => (
              <TournamentCard key={t.id} tournament={t} />
            ))}
          </div>
        </section>
      )}

      {/* Recent completed */}
      {recent.length > 0 && (
        <section>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#111827', margin: 0 }}>Recent Tournaments</h2>
            <Link to="/player/tournaments" style={{ fontSize: '0.8rem', color: '#0D9488', textDecoration: 'none', fontWeight: 500 }}>
              View all
            </Link>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {recent.map(t => (
              <TournamentCard key={t.id} tournament={t} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

function TournamentCard({ tournament: t }) {
  return (
    <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
          <span style={{ fontWeight: 600, color: '#111827', fontSize: '0.95rem' }}>{t.name}</span>
          {statusBadge(t.status)}
        </div>
        <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: 6 }}>
          {t.location && <span>{t.location} · </span>}
          {fmt(t.start_date)}{t.end_date && t.end_date !== t.start_date ? ` – ${fmt(t.end_date)}` : ''}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {t.entries.map((e, i) => (
            <span key={i} style={{ fontSize: '0.72rem', background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: 99, padding: '2px 8px', fontWeight: 500 }}>
              {e.event_name} · {e.entry_type}
            </span>
          ))}
        </div>
      </div>
      <Link
        to={`/t/${t.id}`}
        style={{ fontSize: '0.8rem', color: '#0D9488', textDecoration: 'none', fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        View →
      </Link>
    </div>
  );
}

export default PlayerDashboard;
